/**
 * "Payment Links" block under Events on the unit dashboard.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Link2, Plus, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { PaymentLink } from '@masonic-tools/shared-types';
import { useNow, usePaymentLinks, usePaymentLinkSetup } from '@/hooks/usePaymentLinks';
import { amountRuleLabel, formatPence, linkDisplayState } from '@/lib/unit-payment-links';
import { PaymentLinkStatusBadge, ReferenceChip } from './PaymentLinkStatusBadge';
import { PaymentLinkFormDialog } from './PaymentLinkFormDialog';
import { copyLink, PaymentLinkQrDialog } from './PaymentLinkQrDialog';

export function DashboardPaymentLinksSection({ unitId }: { unitId: string }) {
  const navigate = useNavigate();
  const now = useNow();
  const { links, loading } = usePaymentLinks(unitId);
  const setup = usePaymentLinkSetup(unitId);
  const [formOpen, setFormOpen] = useState(false);
  const [qrLink, setQrLink] = useState<PaymentLink | null>(null);

  const active = useMemo(
    () =>
      links
        .filter((l) => {
          const s = linkDisplayState(l, now);
          return s === 'active' || s === 'expiring';
        })
        .slice(0, 3),
    [links, now],
  );

  return (
    <section className="mt-10" aria-labelledby="dashboard-payment-links">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 id="dashboard-payment-links" className="text-lg font-semibold sm:text-xl">
            Payment Links
          </h3>
          <p className="text-sm text-muted-foreground">Collect charity donations and other payments with a shareable link or QR code.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button variant="outline" className="w-full sm:w-auto" onClick={() => navigate(`/unit/${unitId}/payment-links`)}>
            View all
          </Button>
          <Button className="w-full sm:w-auto" onClick={() => setFormOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Payment Link
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-3 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : active.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-8 text-center">
            <Link2 className="mb-3 h-10 w-10 text-muted-foreground" />
            <p className="font-medium">{links.length === 0 ? 'No payment links yet' : 'No active payment links'}</p>
            <p className="mb-4 text-sm text-muted-foreground">Share a link or QR code to take donations at your next meeting.</p>
            <Button onClick={() => setFormOpen(true)}>
              <Plus className="mr-2 h-4 w-4" /> {links.length === 0 ? 'Create your first payment link' : 'New Payment Link'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3">
          {active.map((link) => (
            <Card
              key={link.id}
              className="min-w-0 cursor-pointer transition-shadow hover:shadow-lg"
              onClick={() => navigate(`/unit/${unitId}/payment-links/${link.id}`)}
            >
              <CardContent className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{link.title}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <ReferenceChip reference={link.reference} />
                      <span>{amountRuleLabel(link)}</span>
                    </div>
                  </div>
                  <PaymentLinkStatusBadge link={link} nowMs={now} className="shrink-0" />
                </div>
                <div className="flex items-end justify-between gap-2">
                  <div>
                    <p className="text-xl font-bold tabular-nums">{formatPence(link.totalCollectedPence)}</p>
                    <p className="text-xs text-muted-foreground">
                      {link.paymentCount} payment{link.paymentCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" aria-label="Copy link" onClick={() => copyLink(link.shortUrl)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" aria-label="Show QR code" onClick={() => setQrLink(link)}>
                      <QrCode className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <PaymentLinkFormDialog unitId={unitId} open={formOpen} onOpenChange={setFormOpen} setup={setup} />
      <PaymentLinkQrDialog link={qrLink} unitName={setup.unitName} open={!!qrLink} onOpenChange={(o) => !o && setQrLink(null)} />
    </section>
  );
}
